import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useMediaUpload, UploadedMedia } from '@/hooks/useMediaUpload';
import { useToast } from '@/hooks/use-toast';
import { resolveStorageUrls } from '@/lib/storage';
import { ImagePlus, X, Loader2 } from 'lucide-react';

interface MediaUploaderProps {
  onMediaChange: (media: UploadedMedia[]) => void;
  media: UploadedMedia[];
  maxFiles?: number;
}

export function MediaUploader({
  onMediaChange,
  media,
  maxFiles = 4,
}: MediaUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, deleteFile } = useMediaUpload();
  const { toast } = useToast();

  useEffect(() => {
    let active = true;
    const paths = media.map((item) => item.storagePath);
    resolveStorageUrls(paths).then((map) => {
      if (active) setPreviewUrls(map);
    });
    return () => {
      active = false;
    };
  }, [media]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check max files limit
    const remainingSlots = maxFiles - media.length;
    if (files.length > remainingSlots) {
      toast({
        title: 'Too many files',
        description: `You can only add ${remainingSlots} more image${remainingSlots !== 1 ? 's' : ''}.`,
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      const uploadedMedia: UploadedMedia[] = [];

      for (const file of files) {
        const uploaded = await uploadFile(file);
        uploadedMedia.push(uploaded);
      }

      onMediaChange([...media, ...uploadedMedia]);
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = async (index: number) => {
    const item = media[index];
    try {
      await deleteFile(item.storagePath);
      const newMedia = media.filter((_, i) => i !== index);
      onMediaChange(newMedia);
    } catch (error: any) {
      toast({
        title: 'Error removing file',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Preview Grid */}
      {media.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {media.map((item, index) => (
            <div key={item.storagePath} className="relative aspect-square group">
              {previewUrls.get(item.storagePath) ? (
                item.fileType === 'video' ? (
                  <video
                    src={previewUrls.get(item.storagePath)}
                    className="w-full h-full object-cover rounded-lg"
                    muted
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={previewUrls.get(item.storagePath)}
                    alt={item.fileName}
                    className="w-full h-full object-cover rounded-lg"
                  />
                )
              ) : (
                <div className="w-full h-full rounded-lg bg-muted/50 animate-pulse" />
              )}
              <button
                onClick={() => handleRemove(index)}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {media.length < maxFiles && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <ImagePlus className="h-4 w-4 mr-2" />
                Add Media ({media.length}/{maxFiles})
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
